/**
 * ProductGrid.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React, { useEffect, useMemo, useState } from 'react';
import ProductCard from './ProductCard';
import LoadingSpinner from './LoadingSpinner';
import PaginationBar from './PaginationBar';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import {
  buildBundleSearchExpansion,
  queryMatchesSearchText,
  productRowSearchHaystack,
  scoreProductSearchRelevance,
  effectiveCollectionOrdinal,
} from '../utils/siteSearch';
import './ProductGrid.css';

const PRODUCTS_URL = '/api/products';

// Tag normalization map - groups similar variations together
const TAG_NORMALIZATION = {
  // Fiddle variations
  'fiddle': 'fiddle',
  'fiddler': 'fiddle',
  'fiddlers': 'fiddle',
  'fiddling': 'fiddle',
  'fiddletune': 'fiddle',
  'fiddle tune': 'fiddle',

  // Cape Breton variations
  'cape breton': 'cape-breton',
  'cape-breton': 'cape-breton',
  'cape bretron': 'cape-breton',
  'breton': 'cape-breton',
  'cape': 'cape-breton',
  'canadian': 'cape-breton',
  'canada': 'cape-breton',

  // Scottish variations (including any with scottish in the name)
  'scottish': 'scottish',
  'scotish': 'scottish',
  'scotland': 'scottish',
  'scots': 'scottish',
  'scottish music': 'scottish',
  'scottish tunes': 'scottish',
  'highland': 'scottish',
  'highlands': 'scottish',

  // Irish variations
  'irish': 'irish',
  'ireland': 'irish',
  'irish music': 'irish',

  // Traditional/Folk/Tune variations - all grouped as "traditional"
  'traditional': 'traditional',
  'trad': 'traditional',
  'folk': 'traditional',
  'tune': 'traditional',
  'tunes': 'traditional',
  'music': 'traditional',

  // Book/Reading variations
  'book': 'books',
  'books': 'books',
  'reading': 'books',

  // Tune/Collection variations
  'tune': 'tune-collections',
  'tunes': 'tune-collections',
  'collection': 'tune-collections',
  'collections': 'tune-collections',
  'more tunes': 'tune-collections',

  // Instruction variations
  'instruction': 'instruction',
  'instructional': 'instruction',
  'tutorial': 'instruction',
  'lesson': 'instruction',
  'lessons': 'instruction',
  'method': 'instruction',

  // Artist names - group common artist names together
  'brenda': 'artists',
  'brenda stubbert': 'artists',
  'paul': 'artists',
  'paul cranford': 'artists',
  'jerry': 'artists',
  'jerry holland': 'artists',
  'kinnon': 'artists',
  'kinnon beaton': 'artists',
  'natalie': 'artists',
  'natalie macmaster': 'artists',
  'ashley': 'artists',
  'ashley macisaac': 'artists',
  'buddy': 'artists',
  'buddy macmaster': 'artists',
  'dan r': 'artists',
  'dan r macdonald': 'artists',
  'winston': 'artists',
  'winston fitzgerald': 'artists',

  // Format Variations
  'video': 'audio-video',
  'dvd': 'audio-video',
  'audio': 'audio-video',
  'cd': 'audio-video',
  'mp3': 'audio-video',
  'digital': 'audio-video',
  'download': 'audio-video',
  'digital download': 'audio-video',
  
  'merchandise': 'merchandise',
  'coffee': 'merchandise',
  'tea': 'merchandise',
  'milk': 'merchandise',
  'merch': 'merchandise',

  'paul-cranford': 'cranford-publications',
  'cranford publications': 'cranford-publications',

  // Miscellaneous items
  'bundle': 'bundles',
};

// Priority tags to show first with specific icons
const PRIORITY_TAG_MAP = {
  // Regions
  'scottish': { icon: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', display: 'Scottish', priority: 1, group: 'regions' },
  'irish': { icon: '🇮🇪', display: 'Irish', priority: 2, group: 'regions' },
  'cape-breton': { icon: '🎻', display: 'Cape Breton', priority: 3, group: 'regions' },
  'celtic': { icon: '☘️', display: 'Celtic', priority: 4, group: 'regions' },

  // Music Style
  'traditional': { icon: '🎼', display: 'Traditional', priority: 10, group: 'musicTypes' },

  // Instruments
  'fiddle': { icon: '🎻', display: 'Fiddle', priority: 11, group: 'musicTypes' },
  'pipe': { icon: '🎵', display: 'Pipes', priority: 12, group: 'musicTypes' },
  'bagpipe': { icon: '🎵', display: 'Bagpipes', priority: 12, group: 'musicTypes' },

  // Tune Types
  'reel': { icon: '🎼', display: 'Reel', priority: 20, group: 'musicTypes' },
  'jig': { icon: '🎵', display: 'Jig', priority: 21, group: 'musicTypes' },
  'strathspey': { icon: '🎶', display: 'Strathspey', priority: 22, group: 'musicTypes' },
  'march': { icon: '🥁', display: 'March', priority: 23, group: 'musicTypes' },
  'hornpipe': { icon: '🎺', display: 'Hornpipe', priority: 24, group: 'musicTypes' },
  'waltz': { icon: '💃', display: 'Waltz', priority: 25, group: 'musicTypes' },
  'polka': { icon: '🎪', display: 'Polka', priority: 26, group: 'musicTypes' },
  'air': { icon: '🎹', display: 'Air/Slow', priority: 27, group: 'musicTypes' },

  // Formats & Collections (Top Section)
  'books': { icon: '📖', display: 'Books', priority: 1, group: 'productTypes' },
  'book': { icon: '📖', display: 'Books', priority: 1, group: 'productTypes' },
  'audio-video': { icon: '💿', display: 'Audio /Video', priority: 2, group: 'productTypes' },
  'bundles': { icon: '🎁', display: 'Bundles', priority: 3, group: 'productTypes' },
  'cranford-publications': { icon: <img src="/images/android-chrome-192x192.png" alt="Cranford Logo" style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle', borderRadius: '50%' }} />, display: 'Cranford Publications', priority: 4, group: 'productTypes' },
  'cape-breton-heritage': { icon: '🎻', display: 'Cape Breton Heritage', priority: 5, group: 'productTypes' },
  'tune-collections': { icon: '🎵', display: 'Tune Collections', priority: 6, group: 'productTypes' },
  'merchandise': { icon: '☕', display: 'Merchandise', priority: 7, group: 'productTypes' },

  // Other specific tags
  'artists': { icon: '👤', display: 'Featured Artists', priority: 40, group: 'artists' },
  'brenda-stubbert': { icon: '🎻', display: 'Brenda Stubbert', priority: 42, group: 'artists' },
  'jerry-holland': { icon: '🎻', display: 'Jerry Holland', priority: 43, group: 'artists' },
  'winston-fitzgerald': { icon: '🎻', display: 'Winston Fitzgerald', priority: 44, group: 'artists' },

  // Skill Levels (other)
  'beginner': { icon: '📘', display: 'Beginner', priority: 50, group: 'other' },
  'advanced': { icon: '📕', display: 'Advanced', priority: 51, group: 'other' },
};

// Normalize a tag to its canonical form
const normalizeTag = (tag) => {
  const tagLower = tag.toLowerCase().trim();

  // First check exact match
  if (TAG_NORMALIZATION[tagLower]) {
    return TAG_NORMALIZATION[tagLower];
  }

  // Then check if tag contains certain keywords
  // Scottish variations - anything with "scottish" or "highland" should be grouped
  if (tagLower.includes('scottish') || tagLower.includes('scotish') ||
    tagLower.includes('highland')) {
    return 'scottish';
  }

  // Irish variations - anything with "irish" should be grouped
  if (tagLower.includes('irish')) {
    return 'irish';
  }

  // Traditional/Folk - if it contains these words (but not bare "tune"/"tunes"/"collection")
  if (tagLower.includes('traditional') || tagLower.includes('folk')) {
    return 'traditional';
  }

  // Tune collections as a product-type signal
  if (tagLower === 'tune' || tagLower === 'tunes' || tagLower === 'tune collection' ||
    tagLower === 'tune collections' || tagLower === 'more tunes') {
    return 'tune-collections';
  }

  // Cape Breton variations (including canadian)
  if (tagLower.includes('breton') || tagLower.includes('cape') ||
    tagLower.includes('canadian') || tagLower.includes('canada')) {
    return 'cape-breton';
  }

  // Fiddle variations
  if (tagLower.includes('fiddle')) {
    return 'fiddle';
  }

  // Artist names - check if it's a known artist name
  const artistNames = [
    'brenda', 'paul', 'jerry', 'kinnon', 'natalie', 'ashley', 'buddy', 'winston', 'dan r',
    'andrea', 'beaton', 'chris langan', 'donegal', 'hugh mcdermott', 'jack perron',
    'jean duval', 'mary larsen', 'packie manus byrne', 'randy miller', 'steve jones',
    'tracey', 'dares', 'scott skinner', 'simon fraser', 'william marshall', 'james scott'
  ];
  if (artistNames.some(name => tagLower.includes(name) || name.includes(tagLower))) {
    return 'artists';
  }

  // Group album/CD related tags
  if (tagLower.includes('album') || tagLower.includes('cd') || tagLower.includes('recording')) {
    return 'misc';
  }

  // Group bundle/collection related tags
  if (tagLower.includes('bundle') || tagLower.includes('collection') || tagLower.includes('heritage series')) {
    return 'books';
  }

  // Group instruction/lesson related tags
  if (tagLower.includes('lesson') || tagLower.includes('tutorial') || tagLower.includes('method') ||
    tagLower.includes('instruction') || tagLower.includes('dare')) {
    return 'instruction';
  }

  // Return original if no match
  return tagLower;
};

// Helper function to get icon for a tag
const getTagIcon = (tag) => {
  const tagLower = tag.toLowerCase().trim();

  // Check priority map first
  if (PRIORITY_TAG_MAP[tagLower]) {
    return PRIORITY_TAG_MAP[tagLower].icon;
  }

  // Fallback icon mapping for other tags
  if (tagLower.includes('fiddle') || tagLower.includes('fiddler')) return '🎻';
  if (tagLower.includes('pipe') || tagLower.includes('bagpipe')) return '🎵';
  if (tagLower.includes('accordion')) return '🪗';
  if (tagLower.includes('whistle')) return '🎶';
  if (tagLower.includes('flute')) return '🎼';
  if (tagLower.includes('guitar')) return '🎸';
  if (tagLower.includes('celtic') || tagLower.includes('gaelic')) return '☘️';
  if (tagLower.includes('traditional') || tagLower.includes('trad') || tagLower.includes('folk')) return '🎼';
  if (tagLower.includes('scottish') || tagLower.includes('highland')) return '🏴󠁧󠁢󠁳󠁣󠁴󠁿';
  if (tagLower.includes('irish')) return '🇮🇪';
  if (tagLower.includes('breton') || tagLower.includes('cape') || tagLower.includes('canadian') || tagLower.includes('canada')) return '🎻';
  if (tagLower === 'artists') return '👤';

  // Default fallback
  return '🏷️';
};

// Helper function to get display name for a tag
const getTagDisplayName = (tag, tagLabels) => {
  const tagLower = tag.toLowerCase().trim();
  if (tagLabels?.[tagLower]) {
    return tagLabels[tagLower];
  }
  if (PRIORITY_TAG_MAP[tagLower]) {
    return PRIORITY_TAG_MAP[tagLower].display;
  }
  return tag.charAt(0).toUpperCase() + tag.slice(1);
};

const ProductGrid = ({
  productsUrl = PRODUCTS_URL,
  sessionKey = 'shop_sessionState',
  filtersStorageKey = 'shopFilters',
  adminMode = false,
  useAuthHeaders = false,
  heading,
}) => {
  const { t } = useLanguage();
  const shop = t.shop;
  const tagLabels = shop.tags;
  const gridHeading = heading || shop.collectionHeading;
  // sessionStorage key for search/page state (short-lived; clears when tab closes)
  const SHOP_SESSION_KEY = sessionKey;
  const _shopSession = (() => {
    try { const r = sessionStorage.getItem(SHOP_SESSION_KEY); return r ? JSON.parse(r) : null; } catch (_) { return null; }
  })();

  const [products, setProducts] = useState([]);
  const bundleSearchExpansion = useMemo(
    () => buildBundleSearchExpansion(products),
    [products],
  );
  const [showInventory, setShowInventory] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showFeatured, setShowFeatured] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState(_shopSession?.searchQuery ?? '');
  const [sortBy, setSortBy] = useState('name');
  const [showInStockOnly, setShowInStockOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(_shopSession?.currentPage ?? 1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  useEffect(() => {
    const loadSettings = () => {
      try {
        const storedSetting = localStorage.getItem('showInventory');
        if (storedSetting !== null) {
          setShowInventory(JSON.parse(storedSetting));
        }
        // Load saved filters
        const savedFilters = localStorage.getItem(filtersStorageKey);
        if (savedFilters) {
          const filters = JSON.parse(savedFilters);
          console.log('📋 Loaded saved filters:', filters);
          if (filters.selectedTypes) setSelectedTypes(filters.selectedTypes);
          if (filters.selectedTags) setSelectedTags(filters.selectedTags);
          if (filters.sortBy) setSortBy(filters.sortBy);
          if (filters.showFeatured !== undefined) setShowFeatured(filters.showFeatured);
          if (filters.showInStockOnly !== undefined) setShowInStockOnly(filters.showInStockOnly);
          if (filters.itemsPerPage) setItemsPerPage(filters.itemsPerPage);
        }
      } catch (error) {
        console.error("Failed to read from localStorage", error);
      }
    };

    const fetchProducts = async () => {
      try {
        // Add cache-busting parameter to ensure fresh data
        const cacheBuster = new Date().getTime();
        const token = useAuthHeaders ? localStorage.getItem('token') : null;
        const productsResponse = await fetch(`${productsUrl}?t=${cacheBuster}`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const productsData = await productsResponse.json();
        // Ensure products is always an array
        if (Array.isArray(productsData)) {
          console.log(`✅ Loaded ${productsData.length} products from API`);
          setProducts(productsData);
        } else {
          console.error("Products data is not an array:", productsData);
          setProducts([]);
        }
      } catch (error) {
        console.error("Failed to fetch products:", error);
        setProducts([]);
      }
    };

    const fetchData = async () => {
      setLoading(true);
      loadSettings(); // Load settings from localStorage
      await fetchProducts();
      setLoading(false);
    };

    fetchData();
  }, [productsUrl, useAuthHeaders]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, showFeatured, selectedTypes, selectedTags, showInStockOnly, sortBy]);

  // Persist search + page to sessionStorage so Back restores them
  useEffect(() => {
    try {
      sessionStorage.setItem(SHOP_SESSION_KEY, JSON.stringify({ searchQuery, currentPage }));
    } catch (_) { /* storage unavailable — silently skip */ }
  }, [searchQuery, currentPage]);

  if (loading) {
    return (
      <div className="shop-layout">
        <div className="tunes-header">
          <h1>{gridHeading}</h1>
          <p>{shop.loadingProducts}</p>
        </div>
        <LoadingSpinner message={shop.loadingProducts} />
      </div>
    );
  }

  // Get unique tags with counts, prioritized and normalized
  const getProductTagsWithCounts = () => {
    const tagCounts = {};
    (products || []).forEach(product => {
      const allTags = [];
      if (product.category) allTags.push(product.category.toLowerCase());
      
      if (product.tags) {
        const splitTags = product.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
        allTags.push(...splitTags);
      }
      
      const uniqueTags = [...new Set(allTags)];
      uniqueTags.forEach(tag => {
        // Normalize the tag to group variations together
        const normalizedTag = normalizeTag(tag);
        tagCounts[normalizedTag] = (tagCounts[normalizedTag] || 0) + 1;
      });
    });

    return Object.entries(tagCounts)
      .map(([tag, count]) => {
        const tagLower = tag.toLowerCase();
        const priority = PRIORITY_TAG_MAP[tagLower]?.priority || 1000;
        return {
          tag,
          count,
          priority,
          icon: getTagIcon(tag),
          displayName: getTagDisplayName(tag, tagLabels)
        };
      })
      .sort((a, b) => {
        // First sort by priority (lower number = higher priority)
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        // Then by count descending
        if (a.count !== b.count) {
          return b.count - a.count;
        }
        // Finally alphabetically
        return a.displayName.localeCompare(b.displayName);
      });
  };

  const getSubTagsFor = (parentTag) => {
    const normalizedParent = normalizeTag(parentTag);
    // Find all products that contain the parentTag
    const subsetProducts = (products || []).filter(product => {
      const allTags = [];
      if (product.category) allTags.push(product.category.toLowerCase());
      if (product.tags) {
        allTags.push(...product.tags.split(',').map(tag => tag.trim()).filter(Boolean));
      }
      return allTags.map(t => normalizeTag(t)).includes(normalizedParent);
    });

    const tagCounts = {};
    subsetProducts.forEach(product => {
      const allTags = [];
      if (product.category) allTags.push(product.category.toLowerCase());
      if (product.tags) {
        allTags.push(...product.tags.split(',').map(tag => tag.trim()).filter(Boolean));
      }
      const uniqueTags = [...new Set(allTags.map(t => normalizeTag(t)))];
      uniqueTags.forEach(tag => {
        if (tag !== normalizedParent) { 
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        }
      });
    });

    return Object.entries(tagCounts)
      .map(([tag, count]) => {
        const tagLower = tag.toLowerCase();
        const priority = PRIORITY_TAG_MAP[tagLower]?.priority || 1000;
        return { tag, count, priority, icon: getTagIcon(tag), displayName: getTagDisplayName(tag, tagLabels) };
      })
      .sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        if (a.count !== b.count) return b.count - a.count;
        return a.displayName.localeCompare(b.displayName);
      });
  };


  const productTagsWithCounts = getProductTagsWithCounts();

  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    saveFiltersToStorage({ sortBy: newSort });
  };

  // Save filters to localStorage
  const saveFiltersToStorage = (updates) => {
    try {
      const current = {
        selectedTypes,
        selectedTags,
        sortBy,
        showFeatured,
        showInStockOnly,
        itemsPerPage,
        ...updates
      };
      localStorage.setItem(filtersStorageKey, JSON.stringify(current));
    } catch (error) {
      console.error('Failed to save filters:', error);
    }
  };

  // Separate featured products - they always show first unless a search is active
  const isSearchActive = Boolean(searchQuery.trim());

  const productMatchesShopFilters = (product) => {
    const matchesSearch = !isSearchActive ||
      queryMatchesSearchText(
        productRowSearchHaystack(product, bundleSearchExpansion),
        searchQuery,
        { title: product.name },
      );

    const matchesFeatured = !showFeatured || !product.featured;

    const matchesType = selectedTypes.length === 0 ||
      selectedTypes.includes(product.category || product.type || 'Bundle');

    let matchesTags = true;
    if (selectedTags.length > 0) {
      const productTags = [];
      if (product.category) productTags.push(normalizeTag(product.category.toLowerCase()));
      if (product.tags) {
        productTags.push(...product.tags.split(',').map(t => normalizeTag(t.trim())));
      }
      const normalizedSelectedTags = selectedTags.map(t => normalizeTag(t));
      matchesTags = normalizedSelectedTags.every(tag => productTags.includes(tag));
    }

    const matchesStock = !showInStockOnly || (product.inventory && product.inventory > 0);

    return matchesSearch && matchesFeatured && matchesType && matchesTags && matchesStock;
  };

  const compareProducts = (a, b) => {
    switch (sortBy) {
      case 'price-low':
        return (a.price_cents ?? a.price) - (b.price_cents ?? b.price);
      case 'price-high':
        return (b.price_cents ?? b.price) - (a.price_cents ?? a.price);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'type':
        return (a.category || a.type).localeCompare(b.category || b.type);
      case 'featured':
        return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      default:
        return 0;
    }
  };

  let filteredAndSortedProducts;

  if (isSearchActive) {
    filteredAndSortedProducts = (products || [])
      .filter(productMatchesShopFilters)
      .sort((a, b) => {
        const scoreDiff =
          scoreProductSearchRelevance(b, searchQuery) -
          scoreProductSearchRelevance(a, searchQuery);
        if (scoreDiff !== 0) return scoreDiff;
        const ordDiff =
          effectiveCollectionOrdinal(a.name) - effectiveCollectionOrdinal(b.name);
        if (ordDiff !== 0) return ordDiff;
        return compareProducts(a, b) || a.name.localeCompare(b.name);
      });
  } else {
    const featuredProducts = (products || [])
      .filter(product => (product.featured === 1 || product.featured === true) && productMatchesShopFilters(product))
      .sort((a, b) => a.name.localeCompare(b.name));

    const filteredNonFeaturedProducts = (products || [])
      .filter(product => {
        if (product.featured === 1 || product.featured === true) return false;
        return productMatchesShopFilters(product);
      })
      .sort(compareProducts);

    filteredAndSortedProducts = [...featuredProducts, ...filteredNonFeaturedProducts];
  }

  const clearAllFilters = () => {
    setSearchQuery('');
    setShowFeatured(false);
    setSelectedTypes([]);
    setSelectedTags([]);
    setShowInStockOnly(false);
    setSortBy('name');
    setCurrentPage(1);
    localStorage.removeItem('shopFilters');
    try { sessionStorage.removeItem(SHOP_SESSION_KEY); } catch (_) { /* ignore */ }
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedProducts.length / itemsPerPage);
  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = filteredAndSortedProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    saveFiltersToStorage({ itemsPerPage: newItemsPerPage });
  };

  const hasActiveFilters = searchQuery || showFeatured || selectedTypes.length > 0 || selectedTags.length > 0 || showInStockOnly || sortBy !== 'name';

  // Debug logging
  console.log('🛒 ProductGrid State:', {
    totalProducts: products.length,
    filteredProducts: filteredAndSortedProducts.length,
    activeFilters: {
      searchQuery,
      showFeatured,
      selectedTypes,
      selectedTags,
      showInStockOnly,
      sortBy
    }
  });

  return (
    <div className="shop-layout">
      {/* Top Bar - Search and Results */}
      <div className="shop-header-compact">
        <div className="search-bar-with-count">
          <div className="search-bar-compact">
            <input
              type="text"
              id="masthead-search"
              className="search-input-compact"
              placeholder={shop.searchPlaceholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
            <div className="search-icon-compact">🔍</div>
          </div>
          <div className="results-count-inline">
            {fmt(
              filteredAndSortedProducts.length === 1 ? shop.productCountOne : shop.productCountMany,
              { count: filteredAndSortedProducts.length }
            )}
          </div>
        </div>
        {hasActiveFilters && (
          <button
            className="clear-filters-btn-compact"
            onClick={clearAllFilters}
            title={shop.clearFiltersTitle}
          >
            ✕ {shop.clear}
          </button>
        )}
      </div>

      {/* Main Content Area */}
      <div className="shop-content-simple">
        {/* Filter Toolbar View */}
        <div className="filter-toolbar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', padding: '0.5rem 0.75rem', background: 'var(--color-surface)', borderRadius: '6px', border: '1px solid var(--color-border)', flexWrap: 'wrap', alignItems: 'center' }}>
          
          {/* Format / Collection Dropdown */}
          <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 180px' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>{shop.formatCollection}</label>
            <select 
              className="sort-select"
              style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px' }}
              value={(() => {
                 const t = selectedTags[0];
                 return (t && (PRIORITY_TAG_MAP[t.toLowerCase()]?.priority < 10)) ? t : '';
              })()}
              onChange={(e) => {
                const val = e.target.value;
                const secondary = selectedTags.find(t => !PRIORITY_TAG_MAP[t.toLowerCase()] || PRIORITY_TAG_MAP[t.toLowerCase()]?.priority >= 10);
                if (!val) {
                  const newTags = secondary ? [secondary] : [];
                  setSelectedTags(newTags);
                  saveFiltersToStorage({ selectedTags: newTags });
                } else {
                  // Keep secondary tag selected when changing primary!
                  const newTags = secondary ? [val, secondary] : [val];
                  setSelectedTags(newTags);
                  saveFiltersToStorage({ selectedTags: newTags });
                }
              }}
            >
              <option value="">{shop.allProducts}</option>
              {productTagsWithCounts
                .filter(({ tag }) => {
                  const info = PRIORITY_TAG_MAP[tag.toLowerCase()];
                  return info && info.priority < 10; 
                })
                .map(t => (
                  <option key={t.tag} value={t.tag}>{t.displayName.replace(/<[^>]+>/g, '')} ({t.count})</option>
                ))
              }
            </select>
          </div>

          {/* Sub-Category Drill-Down Dropdown */}
          {(() => {
            const primaryTag = selectedTags.find(t => PRIORITY_TAG_MAP[t.toLowerCase()]?.priority < 10);
            const secondaryTag = selectedTags.find(t => !PRIORITY_TAG_MAP[t.toLowerCase()] || PRIORITY_TAG_MAP[t.toLowerCase()]?.priority >= 10);
            
            let children = [];
            let dropdownLabel = shop.otherTags;
            let defaultOption = shop.allTags;

            if (primaryTag) {
              const primaryInfo = productTagsWithCounts.find(t => t.tag === primaryTag);
              if (primaryInfo && primaryInfo.count >= 10) {
                children = getSubTagsFor(primaryTag).filter(child => {
                   const info = PRIORITY_TAG_MAP[child.tag.toLowerCase()];
                   return !info || info.priority >= 10;
                });
                const primaryName = primaryInfo.displayName.replace(/<[^>]+>/g, '');
                dropdownLabel = fmt(shop.tagsIn, { name: primaryName });
                defaultOption = fmt(shop.allIn, { name: primaryName });
              }
            } else {
              children = productTagsWithCounts.filter(({ tag }) => {
                 const info = PRIORITY_TAG_MAP[tag.toLowerCase()];
                 return !info || info.priority >= 10;
              });
            }
            
            if (children.length === 0) return null;

            return (
              <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 180px' }}>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>{dropdownLabel}</label>
                <select 
                  className="sort-select"
                  style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px' }}
                  value={secondaryTag || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) {
                      const newTags = primaryTag ? [primaryTag] : [];
                      setSelectedTags(newTags);
                      saveFiltersToStorage({ selectedTags: newTags });
                    } else {
                      const newTags = primaryTag ? [primaryTag, val] : [val];
                      setSelectedTags(newTags);
                      saveFiltersToStorage({ selectedTags: newTags });
                    }
                  }}
                >
                  <option value="">{defaultOption}</option>
                  {children.map(c => (
                    <option key={c.tag} value={c.tag}>{c.displayName.replace(/<[^>]+>/g, '')} ({c.count})</option>
                  ))}
                </select>
              </div>
            );
          })()}

          {/* Sort By Dropdown */}
          <div className="filter-dropdown-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 150px' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>{shop.sortBy}</label>
            <select
              className="sort-select"
              style={{ width: '100%', minWidth: '0', padding: '0.4rem 0.6rem', fontSize: '0.85rem', height: '34px' }}
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
            >
              <option value="name">{shop.sortName}</option>
              <option value="featured">{shop.sortFeatured}</option>
              <option value="price-low">{shop.sortPriceLow}</option>
              <option value="price-high">{shop.sortPriceHigh}</option>
            </select>
          </div>

        </div>

        {/* Products Area */}
        <div className="products-area-simple">


          {/* Product Grid */}
          <div className="product-grid">
            {filteredAndSortedProducts.length === 0 ? (
              <div className="no-products-found">
                <div className="no-products-icon">🎻</div>
                <h3>{shop.noProductsTitle}</h3>
                <p>{shop.noProductsText}</p>
                {hasActiveFilters && (
                  <button onClick={clearAllFilters} className="clear-all-btn-large">
                    🔄 {shop.resetFilters}
                  </button>
                )}
              </div>
            ) : (
              currentProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  showInventory={showInventory}
                  showHiddenBadge={adminMode}
                />
              ))
            )}
          </div>

          {filteredAndSortedProducts.length > 0 && (
            <PaginationBar
              ariaLabel={shop.paginationAria}
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredAndSortedProducts.length}
              indexOfFirst={indexOfFirstProduct + 1}
              indexOfLast={indexOfLastProduct}
              itemLabel={shop.paginationItemLabel}
              pageSize={itemsPerPage}
              pageSizeOptions={[12, 24, 36, 48]}
              onPageChange={handlePageChange}
              onPageSizeChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductGrid; 